import { ScrollText, Blocks, Bot, CalendarDays, Cpu, Home, Landmark, LayoutDashboard, Link2, ListChecks, Mail, Megaphone, MessageSquare, Package, Route, ScanFace, ShieldCheck, SlidersHorizontal, Sparkles, ThumbsUp, Ticket, TrendingUp, Undo2, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { ANALYSIS_ASSISTANT_TITLE } from './pages/analytics/assistant-version'

export interface V2NavItem {
  /**
   * 이동 경로 — v2 페이지는 '/v2/...', 기존 v1 화면으로 보내려면 '/ops/...' 등 절대경로.
   * children 이 있는 부모 항목은 링크가 아니라 토글이며, 이 값은 활성 판정·권한 필터용 prefix 로만 쓰인다(라우트가 아닐 수 있음).
   */
  to: string
  label: string
  icon?: LucideIcon
  /** 항목 우측 카운트 배지 — SideNav 가 키별 컴포넌트를 렌더한다 */
  badge?: 'inquiryNeedsReply'
  /** 2단계 하위 메뉴 (FIKET-428). 부모는 접기/펼치기 버튼, 실제 페이지 링크는 자식만 */
  children?: V2NavItem[]
  /** 활성 판정을 경로 완전 일치로. 자식 중 하나가 부모 경로 자체인 경우('설정 > 일반' = /v2/settings) 하위 경로에서 같이 켜지지 않게 */
  exact?: boolean
}

export interface V2NavGroup {
  /** 섹션 헤더 — 없으면 헤더 없이 항목만 렌더 */
  label?: string
  items: V2NavItem[]
}

/**
 * v2 네비 구성 — v1(nav-items.tsx)과 독립. 필요한 메뉴만 여기에 직접 추가한다.
 *
 * 추가 방법:
 *   { label: '운영', items: [{ to: '/v2/users', label: '회원' }] }
 *   하위 메뉴: { to: '/v2/chatbot', label: '챗봇', children: [{ to: '/v2/chatbot/faqs', label: 'FAQ' }] }
 * 새 페이지는 src/v2/pages/에 만들고 App.tsx의 /v2 children에 라우트를 등록한다.
 *
 * ⚠️ 권한(FIKET-417): 항목의 `to` 는 @fiket/shared ADMIN_MENUS 의 navPath 와 일치해야 MANAGER 에게
 * 권한 필터가 적용된다(useVisibleNav). 레지스트리에 없는 경로는 MANAGER 에게 숨겨지지 않지만 API 는 403 이다.
 * 그룹명(운영/CS/AI/…)은 ADMIN_MENUS 의 group 과 맞춰 권한 관리 다이얼로그 헤더와 일치시킨다.
 */
export const V2_NAV: V2NavGroup[] = [
  {
    items: [
      { to: '/v2', label: '홈', icon: Home },
      { to: '/v2/dashboard', label: '대시보드', icon: LayoutDashboard },
      { to: '/v2/daily-metrics', label: '일별 지표', icon: CalendarDays },
    ],
  },
  {
    label: '운영',
    items: [
      { to: '/v2/announcements', label: '공지 관리', icon: Megaphone },
      { to: '/v2/users', label: '회원', icon: Users },
      { to: '/v2/invitations', label: '청첩장', icon: Mail },
      { to: '/v2/products', label: '상품 관리', icon: Package },
    ],
  },
  {
    label: 'CS',
    items: [
      { to: '/v2/inquiries', label: '고객 문의', icon: MessageSquare, badge: 'inquiryNeedsReply' },
      {
        // 권한 키 'chatbot'(navPath /v2/chatbot) 하나가 하위 4개를 모두 커버한다. /v2/chatbot 자체는 라우트가 아님
        to: '/v2/chatbot',
        label: '챗봇',
        icon: Bot,
        children: [
          { to: '/v2/chatbot/sessions', label: '대화 로그' },
          { to: '/v2/chatbot/faqs', label: 'FAQ' },
          { to: '/v2/chatbot/settings', label: '설정' },
          { to: '/v2/chatbot/playground', label: '테스트 대화' },
        ],
      },
    ],
  },
  {
    label: 'AI',
    items: [
      { to: '/v2/ai-generations', label: 'AI 생성', icon: Sparkles },
      { to: '/v2/ai-ratings', label: 'AI 평가', icon: ThumbsUp },
      { to: '/v2/prompt-blocks', label: '컨셉 블록', icon: Blocks },
      { to: '/v2/ai-models', label: 'AI 모델', icon: Cpu },
      { to: '/v2/ai-usage-logs', label: 'AI 사용량 로그', icon: ScrollText },
      // ADMIN 전용 — 레지스트리 adminOnly(얼굴 원본 열람·전역 설정)
      { to: '/v2/ai-reference-quality', label: '참조사진 품질', icon: ScanFace },
    ],
  },
  {
    label: '커머스',
    items: [
      { to: '/v2/revenue', label: '매출 관리', icon: TrendingUp },
      // 환불률 (FIKET-511) — 결제일 코호트 환불률 일/주/월 추이 + 릴리스 마커
      { to: '/v2/refund-rate', label: '환불률', icon: Undo2 },
      { to: '/v2/promotions', label: '프로모션', icon: Ticket },
      // 스마트스토어 상품 상세용 nid 진입 링크 생성 (FIKET-470)
      { to: '/v2/smartstore-links', label: '스토어 링크', icon: Link2 },
    ],
  },
  {
    label: '마케팅',
    items: [
      { to: '/v2/attribution', label: '유입·광고 분석', icon: Route },
      { to: '/v2/ad-spend', label: '광고비 세부내역', icon: Megaphone },
    ],
  },
  {
    // 경영지원실용 — 월 마감 숫자(매출·광고비·유입) 피벗 표 + CSV (FIKET-436)
    label: '경영지원',
    items: [
      { to: '/v2/finance', label: '월별 리포트', icon: Landmark },
    ],
  },
  {
    label: '분석',
    items: [
      // ADMIN 전용 — 레지스트리 adminOnly (자연어 분석 소스·축·지표 관리)
      { to: '/v2/analytics/assistant', label: ANALYSIS_ASSISTANT_TITLE, icon: Sparkles },
      { to: '/v2/analytics/metrics', label: '지표 정의', icon: ListChecks },
      { to: '/v2/analytics/history', label: '대화 기록', icon: MessageSquare },
      { to: '/v2/analytics/boards', label: '내 대시보드', icon: LayoutDashboard },
    ],
  },
  {
    label: '시스템',
    items: [
      // ADMIN 전용 — 레지스트리 adminOnly 라 MANAGER 에게는 항상 숨김
      { to: '/v2/roles', label: '권한 관리', icon: ShieldCheck },
      {
        // 설정 — 권한 키 'settings'(navPath /v2/settings, ADMIN 전용) 가 하위를 커버한다. /v2/settings 자체가 '일반' 페이지라 exact 로 활성 판정
        to: '/v2/settings',
        label: '설정',
        icon: SlidersHorizontal,
        children: [
          { to: '/v2/settings', label: '일반', exact: true },
          // 관리자 테스트 결제를 매출·환불 집계에서 빼는 계정 목록 (FIKET-485). 권한 키 'revenue-exclusions'
          { to: '/v2/settings/revenue-exclusions', label: '매출 제외 계정' },
        ],
      },
    ],
  },
]
